// Asistente médico (MedGemma vía Orión): dictado → BORRADOR → el médico revisa,
// corrige y FIRMA. Si el modelo no está conectado se dice tal cual; nunca se
// simula un borrador. La IA no diagnostica: redacta lo que el médico dictó.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileSignature, FolderOpen, PlugZap, Stethoscope } from 'lucide-react';

import { Button, Card, EmptyState, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { osActionBtn } from '@/modules/orion-shell/OsApp';

import { govHealthApi } from '../api';
import type { AiDraft } from '../types';

export function MedAssistant() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ['med-model', 'status'], queryFn: govHealthApi.modelStatus, retry: false });
  const patients = useQuery({ queryKey: ['med-model', 'patients'], queryFn: govHealthApi.patients, retry: false });
  const [patientId, setPatientId] = useState('');
  const [dictado, setDictado] = useState('');
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [texto, setTexto] = useState('');
  const [revisado, setRevisado] = useState(false);
  const [cedula, setCedula] = useState('');
  useEffect(() => { if (!patientId && patients.data?.length) setPatientId(patients.data[0].id); }, [patients.data, patientId]);
  const historial = useQuery({ queryKey: ['med-model', 'drafts', patientId], queryFn: () => govHealthApi.drafts(patientId), enabled: !!patientId, retry: false });

  const generar = useMutation({
    mutationFn: () => govHealthApi.createDraft({ patient_id: patientId, text: dictado }),
    onSuccess: (d) => { setDraft(d); setTexto(d.draft_text); setRevisado(false); qc.invalidateQueries({ queryKey: ['med-model', 'drafts'] }); },
  });
  const firmar = useMutation({
    mutationFn: () => govHealthApi.signDraft(draft!.id, texto, cedula),
    onSuccess: (d) => { setDraft(d); qc.invalidateQueries({ queryKey: ['med-model', 'drafts'] }); },
  });
  const descartar = useMutation({
    mutationFn: () => govHealthApi.discardDraft(draft!.id),
    onSuccess: () => { setDraft(null); setTexto(''); qc.invalidateQueries({ queryKey: ['med-model', 'drafts'] }); },
  });
  const conectado = !!status.data?.connected;
  const paciente = useMemo(() => patients.data?.find((p) => p.id === patientId), [patients.data, patientId]);

  return (
    <div className="grid gap-4 lg:grid-cols-5">
      <div className="flex flex-col gap-4 lg:col-span-3">
        <Card title={<span className="inline-flex items-center gap-1.5"><Stethoscope size={16} className="text-primary" /> Asistente médico · MedGemma</span>}
          action={<ModelChip loading={status.isLoading} connected={conectado} />}>
          {status.isLoading ? <Loading label="Consultando el modelo médico…" /> : !conectado ? (
            <div className="rounded-2xl border border-dashed border-border p-4" role="status">
              <p className="flex items-center gap-2 text-sm font-semibold"><PlugZap size={16} className="text-muted-foreground" /> Modelo médico no conectado</p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Esta organización todavía no tiene un servidor de MedGemma configurado, así que no se generan borradores.
                No mostramos ejemplos inventados. Para conectarlo: MedGemma 1.5 4B en un servidor propio con GPU (L4 24 GB o
                similar) con vLLM u Ollama, y su URL en la configuración de la org en Orión.
              </p>
              {status.data?.reason && <p className="mt-2 font-mono text-[11px] text-muted-foreground">motivo: {status.data.reason}</p>}
            </div>
          ) : (
            <p className="text-[13px] text-muted-foreground">
              {status.data?.model} · {status.data?.endpoint_kind}{status.data?.model_version ? ` · v${status.data.model_version}` : ''}.
              Redacta una nota a partir de lo que dictas; tú la revisas, corriges y firmas.
            </p>
          )}

          <div className="mt-4 grid gap-3">
            <label className="grid gap-1 text-sm">
              <span className="font-medium">Paciente (MedOS)</span>
              {patients.isError ? <ErrorNote>Sin permiso para ver pacientes o MedOS no está disponible.</ErrorNote> : (
                <select value={patientId} onChange={(e) => setPatientId(e.target.value)}
                  className="h-11 rounded-xl border border-border bg-background px-3 text-sm">
                  {(patients.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.display_name} · {p.external_id}</option>)}
                </select>
              )}
            </label>
            <label className="grid gap-1 text-sm">
              <span className="font-medium">Dictado de la consulta</span>
              <textarea value={dictado} onChange={(e) => setDictado(e.target.value)} rows={4} disabled={!conectado}
                placeholder={conectado ? 'Ej.: Tos de cinco días, sin fiebre, saturación 97 %…' : 'Disponible cuando el modelo médico esté conectado'}
                className="rounded-xl border border-border bg-background p-3 text-sm disabled:opacity-60" />
            </label>
            <div className="flex flex-wrap gap-2">
              <Button variant="brand" disabled={!conectado || !patientId || !dictado.trim() || generar.isPending} onClick={() => generar.mutate()}>
                {generar.isPending ? 'Redactando…' : 'Redactar borrador'}
              </Button>
              <button type="button" className={osActionBtn} disabled={!patientId}
                onClick={() => navigate(`/m/clinical-cockpit/patients?patient=${patientId}`)}>
                <FolderOpen size={15} /> Abrir expediente
              </button>
            </div>
            {generar.isError && <ErrorNote>{(generar.error as Error).message}</ErrorNote>}
          </div>
        </Card>

        {draft && (
          <Card title={draft.status === 'firmado' ? 'Nota firmada' : 'Borrador'}>
            {draft.status === 'borrador' ? (
              <>
                <p className="mb-2 flex items-center gap-2 rounded-xl bg-amber-500/15 px-3 py-2 text-[13px] font-semibold text-amber-800 dark:text-amber-200">
                  <AlertTriangle size={15} aria-hidden /> Borrador para revisión médica · requiere firma
                </p>
                <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={9} aria-label="Texto del borrador (editable)"
                  className="w-full rounded-xl border border-border bg-background p-3 font-mono text-[13px]" />
                <label className="mt-3 flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={revisado} onChange={(e) => setRevisado(e.target.checked)} className="mt-1 h-4 w-4" />
                  <span>Revisé y corregí este borrador. La nota y la decisión clínica son mías.</span>
                </label>
                <label className="mt-2 grid gap-1 text-sm sm:max-w-xs">
                  <span className="text-muted-foreground">Cédula profesional <span aria-hidden className="text-red-600">*</span></span>
                  <input value={cedula} onChange={(e) => setCedula(e.target.value)} required aria-required="true"
                    inputMode="numeric" autoComplete="off" placeholder="Obligatoria para firmar"
                    className="h-10 rounded-xl border border-border bg-background px-3" />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button variant="brand" disabled={!revisado || !texto.trim() || cedula.trim().length < 6 || firmar.isPending} onClick={() => firmar.mutate()}>
                    <FileSignature size={15} /> Firmar nota
                  </Button>
                  <Button variant="ghost" onClick={() => descartar.mutate()}>Descartar</Button>
                </div>
                {firmar.isError && <ErrorNote>{(firmar.error as Error).message}</ErrorNote>}
              </>
            ) : (
              <>
                <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 size={16} /> Firmada por {draft.signed_by} · {draft.signed_at ? new Date(draft.signed_at).toLocaleString('es-MX') : ''}
                </p>
                <pre className="whitespace-pre-wrap rounded-xl bg-muted/50 p-3 text-[13px]">{draft.final_text}</pre>
                <p className="mt-2 font-mono text-[11px] text-muted-foreground">
                  modelo {draft.model_id} {draft.model_version} · prompt {draft.prompt_sha256.slice(0, 12)}…
                </p>
              </>
            )}
          </Card>
        )}
      </div>

      <div className="flex flex-col gap-4 lg:col-span-2">
        <Card title="Reglas del asistente">
          <ul className="space-y-2 text-[13px] text-muted-foreground">
            <li><strong className="text-foreground">No es de grado clínico.</strong> MedGemma (licencia HAI-DEF) no se usa para decisiones automatizadas de salud.</li>
            <li><strong className="text-foreground">El médico firma.</strong> Solo una persona con rol médico y su cédula profesional; el agente nunca firma.</li>
            <li><strong className="text-foreground">Bitácora inmutable.</strong> Quién firmó, cuándo, modelo, versión y huella del prompt.</li>
            <li><strong className="text-foreground">Imágenes (rayos X, piel):</strong> solo dentro de un protocolo autorizado (COFEPRIS + comité de ética, Nivel B).</li>
          </ul>
        </Card>
        <Card title={paciente ? `Notas de ${paciente.display_name}` : 'Notas'}>
          {historial.isLoading ? <Loading /> : (historial.data?.items.length ?? 0) === 0 ? (
            <EmptyState icon={<FileSignature size={18} />} title="Sin notas del asistente">Las notas firmadas y los borradores aparecen aquí.</EmptyState>
          ) : (
            <ul className="flex flex-col gap-2">
              {historial.data!.items.map((d) => (
                <li key={d.id} className="rounded-xl border border-border p-2.5 text-xs">
                  <span className={`mr-2 rounded-full px-2 py-0.5 font-semibold ${d.status === 'firmado' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : d.status === 'borrador' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>{d.status}</span>
                  {d.created_at ? new Date(d.created_at).toLocaleDateString('es-MX') : ''} · {d.signed_by || 'sin firma'}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function ModelChip({ loading, connected }: { loading: boolean; connected: boolean }) {
  if (loading) return null;
  return connected ? (
    <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">Conectado</span>
  ) : (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">No conectado</span>
  );
}
